import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MediaCard from "@/components/base/MediaCard";
import { mediaList } from "@/mocks/media";
import { useSeo } from "@/hooks/useSeo";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);

  useSeo({
    title: query ? `“${query}”的搜索结果 - 兔六影院` : "影视搜索 - 兔六影院",
    description: query
      ? `兔六影院搜索“${query}”的影视结果，快速找到相关电影和电视剧资源。`
      : "在兔六影院搜索电影、电视剧，支持关键词模糊匹配，快速发现想看的内容。",
    keywords: "兔六影院,影视搜索,电影搜索,电视剧搜索,影视资源",
  });

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return mediaList.filter(
      (m) =>
        m.title.toLowerCase().includes(q) ||
        m.director.toLowerCase().includes(q) ||
        m.cast.some((c) => c.toLowerCase().includes(q)) ||
        m.genres.some((g) => g.toLowerCase().includes(q)) ||
        m.synopsis.toLowerCase().includes(q)
    );
  }, [query]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setSearchParams(q ? { q } : {}, { replace: true });
  };

  return (
    <div>
      <div className="bg-primary-900 py-8 md:py-12">
        <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8">
          <h1 className="font-heading text-2xl font-black text-background-50 md:text-3xl">
            影视搜索
          </h1>
          <p className="mt-2 text-sm text-primary-200">
            输入片名、演员或题材关键词，快速定位你想要的影视内容
          </p>

          <form onSubmit={submit} className="mt-5 flex max-w-xl items-center gap-2">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="例如：星际、悬疑、陈默然"
                className="h-11 w-full rounded-md border border-primary-700 bg-background-50 pl-10 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-accent-400 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              className="h-11 rounded-md bg-accent-500 px-5 text-sm font-bold text-primary-950 transition-colors hover:bg-accent-400 cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8 py-8 md:py-10">
        {query ? (
          <p className="text-sm text-foreground-600">
            关键词
            <span className="mx-1 font-bold text-foreground-900">“{query}”</span>
            共找到
            <span className="mx-1 font-bold text-accent-600">{results.length}</span>
            条相关结果
          </p>
        ) : (
          <p className="text-sm text-foreground-600">
            还没有输入关键词，试试搜索你想看的电影或电视剧吧。
          </p>
        )}

        {query && results.length === 0 ? (
          <div className="mt-6 rounded-lg border border-background-200 bg-background-100 py-20 text-center">
            <i className="ri-search-eye-line text-3xl text-foreground-400"></i>
            <p className="mt-3 text-base font-medium text-foreground-800">
              没有找到与“{query}”相关的内容
            </p>
            <p className="mt-2 text-sm text-foreground-600">
              换个关键词试试，或者返回首页浏览热门推荐。
            </p>
            <Link
              to="/"
              className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </div>
        ) : results.length > 0 ? (
          <div className="mt-6 grid grid-cols-2 gap-3 md:gap-5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {results.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-2 gap-3 md:gap-5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {mediaList
              .slice()
              .sort((a, b) => b.rating - a.rating)
              .slice(0, 6)
              .map((item) => (
                <MediaCard key={item.id} item={item} showSynopsis={false} />
              ))}
          </div>
        )}
      </div>
    </div>
  );
}